// Менеджер згоди користувачів для Google Consent Mode
// Управляє баннером cookie та оновленням статусу згоди

import { consentTranslations } from '../i18n/consentTranslations.js';

class ConsentManager {
  constructor() {
    this.currentLang = this.detectLanguage();
    this.translations =
      consentTranslations[this.currentLang] || consentTranslations.uk;
    this.consentGiven = this.checkStoredConsent();
    this.init();
  }

  // Визначення поточної мови з URL
  detectLanguage() {
    const path = window.location.pathname;
    const langMatch = path.match(/^\/([a-z]{2})\//);
    return langMatch ? langMatch[1] : 'uk'; // За замовчуванням українська
  }

  // Перевіряємо збережену згоду
  checkStoredConsent() {
    const consent = localStorage.getItem('user_consent');
    return consent ? JSON.parse(consent) : null;
  }

  // Ініціалізація менеджера згоди
  init() {
    if (this.consentGiven) {
      // Якщо згода вже надана, оновлюємо статус
      this.updateConsent(this.consentGiven);
    } else {
      // Показуємо баннер згоди
      this.showConsentBanner();
    }
  }

  // Показ баннера згоди
  showConsentBanner() {
    // Перевіряємо, чи баннер вже існує
    if (document.getElementById('consent-banner')) {
      return;
    }

    const banner = document.createElement('div');
    banner.id = 'consent-banner';
    banner.className = 'consent-banner';

    banner.innerHTML = `
      <div class="consent-content">
        <div class="consent-text">
          <h3>${this.translations.consent_title}</h3>
          <p>${this.translations.consent_description}</p>
        </div>
        <div class="consent-buttons">
          <button id="accept-all" class="consent-btn accept">${this.translations.consent_accept_all}</button>
          <button id="accept-necessary" class="consent-btn necessary">${this.translations.consent_necessary_only}</button>
          <button id="customize-consent" class="consent-btn customize">${this.translations.consent_customize}</button>
        </div>
      </div>
    `;

    document.body.appendChild(banner);

    // Додаємо обробники подій
    this.attachEventListeners();
  }

  // Додаємо обробники подій для кнопок
  attachEventListeners() {
    const acceptAllBtn = document.getElementById('accept-all');
    const necessaryBtn = document.getElementById('accept-necessary');
    const customizeBtn = document.getElementById('customize-consent');

    if (acceptAllBtn) {
      acceptAllBtn.addEventListener('click', () => this.acceptAll());
    }

    if (necessaryBtn) {
      necessaryBtn.addEventListener('click', () => this.acceptNecessary());
    }

    if (customizeBtn) {
      customizeBtn.addEventListener('click', () => this.showConsentModal());
    }
  }

  // Показ кастомного модального вікна
  showCustomizeModal() {
    this.showConsentModal();
  }

  // Додаємо обробники для модального вікна
  attachModalEventListeners() {
    const closeBtn = document.querySelector('.consent-modal-close');
    const saveBtn = document.getElementById('save-preferences');
    const acceptAllBtn = document.getElementById('accept-all-modal');
    const modal = document.getElementById('consent-modal');

    if (closeBtn) {
      closeBtn.addEventListener('click', () => this.hideConsentModal());
    }

    if (saveBtn) {
      saveBtn.addEventListener('click', () => this.saveCustomConsent());
    }

    if (acceptAllBtn) {
      acceptAllBtn.addEventListener('click', () => {
        this.acceptAll();
        this.hideConsentModal();
      });
    }

    // Закриття модального вікна при кліку поза ним
    if (modal) {
      modal.addEventListener('click', e => {
        if (e.target === modal) {
          this.hideConsentModal();
        }
      });
    }
  }

  // Прийняти всі cookie
  acceptAll() {
    const consent = {
      analytics_storage: 'granted',
      ad_storage: 'granted',
      ad_user_data: 'granted',
      ad_personalization: 'granted',
      personalization_storage: 'granted',
      functionality_storage: 'granted',
      security_storage: 'granted',
    };

    this.updateConsent(consent);
    this.hideConsentBanner();
  }

  // Прийняти тільки необхідні cookie
  acceptNecessary() {
    const consent = {
      analytics_storage: 'denied',
      ad_storage: 'denied',
      ad_user_data: 'denied',
      ad_personalization: 'denied',
      personalization_storage: 'denied',
      functionality_storage: 'granted',
      security_storage: 'granted',
    };

    this.updateConsent(consent);
    this.hideConsentBanner();
  }

  // Показ модального вікна для детальних налаштувань
  showConsentModal() {
    const modal = document.createElement('div');
    modal.id = 'consent-modal';
    modal.className = 'consent-modal';

    // Отримуємо поточний стан згоди (за замовчуванням всі активні)
    const currentConsent = this.consentGiven || {
      analytics_storage: 'granted',
      ad_storage: 'granted',
    };

    modal.innerHTML = `
      <div class="consent-modal-content">
        <div class="consent-modal-header">
          <h2>${this.translations.consent_modal_title}</h2>
          <button class="consent-modal-close" aria-label="${
            this.translations.consent_close
          }">&times;</button>
        </div>

        <div class="consent-modal-body">
          <div class="consent-section">
            <h3>${this.translations.consent_necessary_title}</h3>
            <p>${this.translations.consent_necessary_description}</p>
            <div class="consent-toggle">
              <input type="checkbox" id="necessary-toggle" checked disabled>
              <label for="necessary-toggle">${
                this.translations.consent_always_active
              }</label>
            </div>
          </div>

          <div class="consent-section">
            <h3>${this.translations.consent_analytics_title}</h3>
            <p>${this.translations.consent_analytics_description}</p>
            <div class="consent-toggle">
              <input type="checkbox" id="analytics_storage_opt" ${
                currentConsent.analytics_storage === 'granted' ? 'checked' : ''
              }>
              <label for="analytics_storage_opt">Google Analytics</label>
            </div>
          </div>

          <div class="consent-section">
            <h3>${this.translations.consent_advertising_title}</h3>
            <p>${this.translations.consent_advertising_description}</p>
            <div class="consent-toggle">
              <input type="checkbox" id="ad_storage_opt" ${
                currentConsent.ad_storage === 'granted' ? 'checked' : ''
              }>
              <label for="ad_storage_opt">Google Ads</label>
            </div>
          </div>
        </div>

        <div class="consent-modal-footer">
          <button id="save-preferences" class="consent-btn save">${
            this.translations.consent_save
          }</button>
          <button id="accept-all-modal" class="consent-btn accept">${
            this.translations.consent_accept_all
          }</button>
        </div>
      </div>
    `;

    document.body.appendChild(modal);

    // Додаємо обробники для модального вікна
    this.attachModalEventListeners();
  }

  // Збереження користувацьких налаштувань згоди
  saveCustomConsent() {
    const analyticsChecked = document.getElementById(
      'analytics_storage_opt'
    ).checked;
    const adsChecked = document.getElementById('ad_storage_opt').checked;

    const consent = {
      analytics_storage: analyticsChecked ? 'granted' : 'denied',
      ad_storage: adsChecked ? 'granted' : 'denied',
      ad_user_data: adsChecked ? 'granted' : 'denied',
      ad_personalization: adsChecked ? 'granted' : 'denied',
      personalization_storage: adsChecked ? 'granted' : 'denied',
      functionality_storage: 'granted',
      security_storage: 'granted',
    };

    this.updateConsent(consent);
    this.hideConsentModal();
    this.hideConsentBanner();
  }

  // Оновлення статусу згоди в Google
  updateConsent(consent) {
    if (typeof gtag !== 'undefined') {
      gtag('consent', 'update', consent);
    }

    // Зберігаємо згоду в localStorage
    localStorage.setItem('user_consent', JSON.stringify(consent));
    this.consentGiven = consent;
  }

  // Приховування баннера згоди
  hideConsentBanner() {
    const banner = document.getElementById('consent-banner');
    if (banner) {
      banner.remove();
    }
  }

  // Приховування модального вікна
  hideConsentModal() {
    const modal = document.getElementById('consent-modal');
    if (modal) {
      modal.remove();
    }
  }

  // Публічний метод для скидання згоди (для налаштувань)
  resetConsent() {
    localStorage.removeItem('user_consent');
    this.consentGiven = null;
    this.showConsentBanner();
  }
}

// Експорт для використання в інших файлах
export default ConsentManager;

// Автоматична ініціалізація при завантаженні DOM
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () => {
    window.consentManager = new ConsentManager();
  });
} else {
  window.consentManager = new ConsentManager();
}
